/**
 * refusalFields — which field a refusal is about.
 *
 * §A4 SAYS THE SERVER'S SENTENCE GOES UNDER THE OFFENDING FIELD, and every piece of machinery for
 * that already existed: the reducer carries `fieldErrors`, `QuietField` renders the sentence and
 * turns the row `.sd-errored` with `aria-errormessage`, the eyebrow counts `2 fields rejected`.
 * The one thing missing was anybody ever populating the map — so every refusal, including the ones
 * whose text names exactly one field, landed at the bottom of the block as an undifferentiated
 * line. This module is that map.
 *
 * WHY A MAP AND NOT A PARSER. The writes already speak in whole sentences an admin can act on
 * ("Enter the date the work started."), and the sentences are constants: the validator that raises
 * one is the same module that owns the field name it is about. Matching them EXACTLY is what keeps
 * this honest — a PostgREST constraint sentence, a network failure or a message somebody edits
 * without editing this file all miss, come back unattributed, and land on the group's own line
 * exactly as they do today. Fuzzy matching would put a foreign-key error under an arbitrary input.
 *
 * ONE MAP PER WRITE, NOT ONE MAP FOR THE CANVAS, and the collisions are why: `Select a start date`
 * is raised by the contract instalment (field `instalmentStart`), by the insurance loan's recovery
 * (field `instalmentStart`) and by the policy period (field `startsAt`). The sentence alone does
 * not name a field; the sentence plus the write does.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import {
    NOT_PDF_REFUSAL_PREFIX,
    NOT_STATUTORY_REFUSAL_PREFIX,
    NO_IDENTIFIER_REFUSAL_PREFIX,
    TOO_LARGE_REFUSAL_PREFIX,
    UNREADABLE_REFUSAL_PREFIX,
    VEHICLE_UNRECORDED_REFUSAL_PREFIX,
} from '@/lib/certificatePrefill';
import { VEHICLE_REFUSAL_PREFIX } from '@/lib/certificateVehicle';

/** Refusal sentence → the draft field it belongs under, for one write. */
export type RefusalFieldMap = Readonly<Record<string, string>>;

/**
 * Refusal OPENING → the field, for the refusals whose sentences are not constants.
 *
 * THE EXCEPTION IS NARROW AND STATED, BECAUSE EXACT MATCHING IS THE THING THAT KEEPS THIS HONEST.
 * Every sentence on this canvas is a constant except the certificate family: two of its seven name
 * their own values — the identifiers that disagree, and whichever identifier the vehicle record is
 * missing — so those values are IN the sentence and no map can hold them. A prefix is the smallest
 * thing that recognises one: the opening is a phrase nothing else on this canvas says, and the
 * sentence it opens is the only sentence that can follow it. Prefixes are tried only AFTER every
 * exact key has missed, so one can never shadow a constant.
 */
export type RefusalPrefixMap = Readonly<Record<string, string>>;

/**
 * The field-scoped refusals for one raised message, or undefined when nothing claims it.
 *
 * UNDEFINED IS A REAL ANSWER, not a failure: it is what leaves the verbatim sentence on the
 * group's own line, which is where an unattributable refusal belongs.
 */
export function attributeRefusal(
    map: RefusalFieldMap,
    message: string | null | undefined,
    prefixes?: RefusalPrefixMap,
): Record<string, string> | undefined {
    const sentence = (message ?? '').trim();
    if (sentence === '') return undefined;
    const field = map[sentence];
    if (field !== undefined) return { [field]: sentence };
    for (const [opening, prefixed] of Object.entries(prefixes ?? {})) {
        if (sentence.startsWith(opening)) return { [prefixed]: sentence };
    }
    return undefined;
}

/** The `fieldErrorsFor` a group is wired with: `useInlineGroup({ …, fieldErrorsFor: … })`. */
export function fieldErrorsFrom(
    map: RefusalFieldMap,
    prefixes?: RefusalPrefixMap,
): (error: Error) => Record<string, string> | undefined {
    return (error: Error) => attributeRefusal(map, error?.message, prefixes);
}

// ─── Vehicle ─────────────────────────────────────────────────────────────────

/** `validateRegistrationDraft`, raised through `saveVehicleRegistration`. */
export const REGISTRATION_REFUSALS: RefusalFieldMap = {
    'Enter the registration number — it prints on the hire purchase agreement.': 'number',
    'Choose how this vehicle was registered.': 'type',
    'Link the payment for the registration — the record cannot be saved without one.':
        'transactionId',
};

/** `saveRoadworthiness`. The certificate is the gate, so its refusal sits on the upload row. */
export const ROADWORTHINESS_REFUSALS: RefusalFieldMap = {
    'Enter the date the certificate expires.': 'expiresAt',
    'Upload the certificate — the record cannot be saved without the document.': 'certificate',
};

/** `validateServiceDraft`, raised through `saveMaintenance`. */
export const SERVICE_REFUSALS: RefusalFieldMap = {
    'Describe the work that was done.': 'description',
    'Enter the date the work started.': 'startedAt',
    // The end date is the one that moved — the start is the fact it is being measured against.
    'The work cannot have finished before it started.': 'endedAt',
    'Link the payment for this work — the record cannot be saved without one.': 'transactionId',
};

/** `saveVehicleMileage`. */
export const ODOMETER_REFUSALS: RefusalFieldMap = {
    'Please enter a valid mileage': 'mileage',
};

// ─── Driver ──────────────────────────────────────────────────────────────────

/**
 * `saveDriverDocument`.
 *
 * ITS ONE REFUSAL IS ABOUT THE SLOT, NOT THE EXPIRY — an admin who types a date onto a document
 * that is not on file gets this back, and the field to fix is the upload beside it.
 */
export const DRIVER_DOC_REFUSALS: RefusalFieldMap = {
    'Choose the document file first.': 'document',
};

// ─── Insurance ───────────────────────────────────────────────────────────────

/** `validatePolicyDraft`, raised through `savePolicyDetails`. */
export const POLICY_REFUSALS: RefusalFieldMap = {
    'Enter a valid insured value': 'valuedAt',
    'Select a start date': 'startsAt',
    'Select an expiry date': 'expiresAt',
};

/**
 * THE CERTIFICATE REFUSALS — all seven of them, raised by `savePolicyDetails`'s own guard rather
 * than by `validatePolicyDraft`, which is why `POLICY_REFUSALS` is still three and the docblock at
 * `policyDraft.ts:31-37` still holds.
 *
 * THEY ARE PREFIXES AND NOT CONSTANTS BECAUSE TWO OF THE SEVEN NAME THEIR OWN VALUES — the mismatch
 * sentence prints the identifiers that disagree, and the vehicle-unrecorded one names whichever
 * identifier is actually missing — so no exact-match map can hold them. The other five are
 * constants, and they are matched the same way rather than a second mechanism being introduced for
 * five sentences out of one author (`certificateRefusal`): one family, one matcher. Every opening
 * is a phrase nothing else on this canvas says, none is a prefix of another, and all seven are
 * tried only AFTER every exact key has missed, so a prefix can never shadow a constant.
 *
 * ALL SEVEN LAND ON THE UPLOAD ROW, INCLUDING THE ONE ABOUT THE VEHICLE. The document is what is
 * wrong with the save in every case — the vehicle-unrecorded refusal is raised because a
 * certificate was staged, and taking it back out is what lets the rest of the block save — and the
 * field it names is two areas away, where no `fieldErrors` map on this group can reach.
 */
export const POLICY_REFUSAL_PREFIXES: RefusalPrefixMap = {
    [VEHICLE_REFUSAL_PREFIX]: 'certificate',
    [UNREADABLE_REFUSAL_PREFIX]: 'certificate',
    [NOT_PDF_REFUSAL_PREFIX]: 'certificate',
    [TOO_LARGE_REFUSAL_PREFIX]: 'certificate',
    [NOT_STATUTORY_REFUSAL_PREFIX]: 'certificate',
    [NO_IDENTIFIER_REFUSAL_PREFIX]: 'certificate',
    [VEHICLE_UNRECORDED_REFUSAL_PREFIX]: 'certificate',
};

/**
 * `savePremiumLink` and `saveDiscountLink`.
 *
 * "Link a policy before recording what was paid for it." IS DELIBERATELY ABSENT. It is not about
 * either row on this block — it is about the picker one block up — and putting it under the
 * premium row would send an admin to a field that is already correct.
 */
export const PREMIUM_REFUSALS: RefusalFieldMap = {
    'Select the payment made to the insurer.': 'premium',
    'Select the credit the insurer returned.': 'discount',
    // Raised by the DISCOUNT write, but the field to fix is the premium it hangs off.
    'Record the premium payment before its discount.': 'premium',
};

/** `saveInsuranceLoan` and `saveLoanInstalment`. */
export const INSURANCE_LOAN_REFUSALS: RefusalFieldMap = {
    'Enter a valid loan amount': 'amount',
    'Enter a valid interest rate': 'rate',
    'Enter a valid instalment amount': 'instalment',
    'Select a start date': 'instalmentStart',
    'Record the loan before its weekly recovery.': 'amount',
};

// ─── Deposit ─────────────────────────────────────────────────────────────────

/** `createContractDeposit`. */
export const DEPOSIT_RECORD_REFUSALS: RefusalFieldMap = {
    'Select the bank transaction this deposit was paid on.': 'transactionId',
};

// ─── Financials ──────────────────────────────────────────────────────────────

/**
 * The Money particulars — Cash price, Hire-purchase price and Weekly Instalment:
 * `saveCashPrice`, `saveSalePrice` and `saveContractInstalment`, one commit.
 */
export const CLAUSE_REFUSALS: RefusalFieldMap = {
    'Enter a valid cash price': 'cashPrice',
    'Enter a valid sale price': 'price',
    'Enter a valid instalment amount': 'instalmentAmount',
    'Select a start date': 'instalmentStart',
};

/** Clause 2.3: `saveOverdueRate`. */
export const OVERDUE_RATE_REFUSALS: RefusalFieldMap = {
    'The overdue interest rate cannot be blank — it prints on clause 2.3': 'overdueRate',
    'Enter a valid overdue interest rate': 'overdueRate',
};

/** The internal block: `saveManagementRate`. */
export const MANAGEMENT_RATE_REFUSALS: RefusalFieldMap = {
    'Enter a valid percentage (0-100)': 'managementRate',
};
